// components/Sections/Admin/Matriculas/UsuariosMatriculados.jsx
//
// Matrículas de un recinto. Todo lo que se ve (tarjetas, filtros, exportación) sale de
// la misma lista que devuelve `GET /matriculas/usuarios/:recintoId`, que trae todas las
// matrículas con su estado. Antes:
//  · las tarjetas leían `response.data` de un servicio que YA devuelve `data`, así que
//    quedaban vacías (y además esperaban campos que el backend no envía);
//  · el filtro de estado mandaba `vigente=`, un campo que el modelo no tiene: con él la
//    consulta no encontraba nada;
//  · el historial leía `response.data` por lo mismo y salía siempre vacío;
//  · cada tecla de la búsqueda (que es local) volvía a pedir la lista y las estadísticas.
import React, { useState, useEffect, useMemo } from 'react';
import { Table, Input, Select, Modal, Descriptions, Alert, Dropdown, Tooltip, Spin } from 'antd';
import { EyeOutlined, HistoryOutlined, StopOutlined, WarningOutlined } from '@ant-design/icons';
import {
  FiSearch, FiDownload, FiMoreVertical, FiUsers, FiCheckCircle, FiClock, FiXCircle, FiRefreshCw,
} from 'react-icons/fi';
import dayjs from 'dayjs';
import MatriculaService from '../../../../service/Matriculas';
import RecintoService from '../../../../service/Recintos';
import { useNotify } from '../../../../providers/NotificationProvider';
import useAuthContext from '../../../../hooks/useAuthContext';
import { can } from '../../../../config/rbac';
import { exportarHojaUnica } from '../../../../utils/exportExcel';
import { formatearNombre } from '../../../../utils/texto';
import {
  EncabezadoAdmin, TarjetaResumen, FiltroChips, Iniciales, claseBtnSecundario,
} from '../../../Ui/Admin/PiezasAdmin';
import { DIAS_POR_VENCER, ESTADOS, estadoDe, sigueActiva, tipoRegistro, textoVigencia, textoOrigen } from './estadoMatricula';

const ChipEstadoMatricula = ({ m }) => {
  const e = ESTADOS[estadoDe(m)];
  const chip = (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold whitespace-nowrap ${e.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${e.punto}`} /> {e.label}
    </span>
  );
  return estadoDe(m) === 'revocacion_sin_aplicar'
    ? <Tooltip title="Se registró la revocación, pero el servidor no la aplicó: la matrícula sigue activa.">{chip}</Tooltip>
    : chip;
};

const formatearMoneda = (monto) => new Intl.NumberFormat('es-CL', {
  style: 'currency', currency: 'CLP', minimumFractionDigits: 0,
}).format(monto || 0);

const vencimientoTexto = (m) => {
  if (m.tipoVigencia === 'indefinida' || !m.fechaVencimiento) return { fecha: 'Indefinida', detalle: 'Sin vencimiento' };
  const v = dayjs(m.fechaVencimiento);
  const dias = v.startOf('day').diff(dayjs().startOf('day'), 'day');
  return {
    fecha: v.format('DD/MM/YYYY'),
    detalle: dias < 0 ? `hace ${Math.abs(dias)} día${Math.abs(dias) !== 1 ? 's' : ''}`
      : dias === 0 ? 'vence hoy' : `en ${dias} día${dias !== 1 ? 's' : ''}`,
    dias,
  };
};

const UsuariosMatriculados = () => {
  const notify = useNotify();
  const { user } = useAuthContext();
  const puedeRevocar = can(user, 'matriculas', 'editar');

  const [recintos, setRecintos] = useState([]);
  const [recintoId, setRecintoId] = useState(null);
  const [matriculas, setMatriculas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [busqueda, setBusqueda] = useState('');
  const [vista, setVista] = useState('todas'); // todas | activas | por_vencer | inactivas
  const [registro, setRegistro] = useState('todos'); // todos | pago | manual

  const [detalle, setDetalle] = useState(null);
  const [historial, setHistorial] = useState({ abierto: false, cargando: false, persona: null, filas: [] });
  const [revocar, setRevocar] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [revocando, setRevocando] = useState(false);

  useEffect(() => {
    RecintoService.List()
      .then(res => {
        const lista = (res.data || []).slice().sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
        setRecintos(lista);
        if (lista.length) setRecintoId(lista[0]._id);
        else setLoading(false);
      })
      .catch(() => { notify.error('Error al cargar recintos'); setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recarga manual (botón y tras revocar). Filtros y búsqueda son locales.
  const cargar = async (id = recintoId) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const lista = await MatriculaService.ListarUsuariosMatriculados(id);
      setMatriculas(Array.isArray(lista) ? lista : []);
    } catch (e) {
      setError(e?.error || e?.message || 'No se pudieron cargar las matrículas');
      setMatriculas([]);
    } finally {
      setLoading(false);
    }
  };
  // Una consulta por recinto. `vigente` descarta la respuesta de un recinto anterior
  // si llega tarde (al cambiar rápido de recinto).
  useEffect(() => {
    let vigente = true;
    if (!recintoId) return undefined;
    setLoading(true);
    setError(null);
    MatriculaService.ListarUsuariosMatriculados(recintoId)
      .then(lista => { if (vigente) setMatriculas(Array.isArray(lista) ? lista : []); })
      .catch(e => { if (vigente) { setError(e?.error || e?.message || 'No se pudieron cargar las matrículas'); setMatriculas([]); } })
      .finally(() => { if (vigente) setLoading(false); });
    return () => { vigente = false; };
  }, [recintoId]);

  const conteo = useMemo(() => {
    const est = matriculas.map(estadoDe);
    return {
      total: matriculas.length,
      activas: est.filter(e => e === 'vigente' || e === 'por_vencer').length,
      porVencer: est.filter(e => e === 'por_vencer').length,
      inactivas: est.filter(e => !['vigente', 'por_vencer'].includes(e)).length,
    };
  }, [matriculas]);

  const q = busqueda.trim().toLowerCase();
  const filtradas = matriculas.filter(m => {
    const e = estadoDe(m);
    const okVista = vista === 'todas'
      || (vista === 'activas' && (e === 'vigente' || e === 'por_vencer'))
      || (vista === 'por_vencer' && e === 'por_vencer')
      || (vista === 'inactivas' && !['vigente', 'por_vencer'].includes(e));
    const okRegistro = registro === 'todos' || tipoRegistro(m) === registro;
    const okBusqueda = !q
      || m.datosInscrito?.nombre?.toLowerCase().includes(q)
      || m.datosInscrito?.email?.toLowerCase().includes(q)
      || m.rut?.toLowerCase().includes(q);
    return okVista && okRegistro && okBusqueda;
  });
  const hayFiltros = q || vista !== 'todas' || registro !== 'todos';
  const nombreRecinto = recintos.find(r => r._id === recintoId)?.nombre || '';

  const verHistorial = async (m) => {
    setHistorial({ abierto: true, cargando: true, persona: m, filas: [] });
    try {
      const filas = await MatriculaService.ObtenerHistorialUsuario(m.rut);
      setHistorial(h => ({ ...h, cargando: false, filas: Array.isArray(filas) ? filas : [] }));
    } catch (e) {
      notify.error(e?.error || 'Error al cargar el historial');
      setHistorial(h => ({ ...h, cargando: false }));
    }
  };

  const confirmarRevocacion = async () => {
    const texto = motivo.trim();
    if (texto.length < 5) { notify.error('El motivo debe tener al menos 5 caracteres'); return; }
    setRevocando(true);
    try {
      await MatriculaService.DesactivarMatriculaUsuario(revocar._id, texto);
      notify.success('Revocación registrada');
      setRevocar(null);
      setMotivo('');
      setDetalle(null);
      cargar();
    } catch (e) {
      notify.error(e?.error || e?.message || 'Error al revocar la matrícula');
    } finally {
      setRevocando(false);
    }
  };

  const columns = [
    {
      title: 'Persona',
      key: 'persona',
      width: 320,
      sorter: (a, b) => (a.datosInscrito?.nombre || '').localeCompare(b.datosInscrito?.nombre || '', 'es'),
      render: (_, m) => (
        <div className="flex items-center gap-3 min-w-0">
          <Iniciales nombre={m.datosInscrito?.nombre} />
          <div className="min-w-0">
            <div className="font-bold text-gray-900 truncate">{formatearNombre(m.datosInscrito?.nombre) || 'Sin nombre'}</div>
            <div className="text-xs text-gray-500 truncate">
              <span className="font-mono">{m.rut}</span>{m.datosInscrito?.email && <> · {m.datosInscrito.email}</>}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Estado',
      key: 'estado',
      width: 190,
      render: (_, m) => <ChipEstadoMatricula m={m} />,
    },
    {
      title: 'Vencimiento',
      key: 'vencimiento',
      width: 170,
      sorter: (a, b) => new Date(a.fechaVencimiento || '2999-01-01') - new Date(b.fechaVencimiento || '2999-01-01'),
      render: (_, m) => {
        const v = vencimientoTexto(m);
        return (
          <div>
            <div className="text-sm font-semibold text-gray-900 tabular-nums">{v.fecha}</div>
            <div className={`text-xs ${v.dias !== undefined && v.dias < 0 ? 'text-red-600' : v.dias !== undefined && v.dias <= DIAS_POR_VENCER ? 'text-[#92400e] font-semibold' : 'text-gray-500'}`}>
              {v.detalle}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Registro',
      key: 'registro',
      width: 170,
      render: (_, m) => (
        tipoRegistro(m) === 'pago' ? (
          <div>
            <div className="text-sm font-bold text-gray-900 tabular-nums">{formatearMoneda(m.valorPagado)}</div>
            <div className="text-xs text-gray-500">Pago online</div>
          </div>
        ) : (
          <div>
            <span className="inline-block px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-bold">Manual</span>
            <div className="text-xs text-gray-500 mt-0.5">Sin pago asociado</div>
          </div>
        )
      ),
    },
    {
      title: 'Registrada',
      dataIndex: 'fechaInicio',
      key: 'fechaInicio',
      width: 120,
      sorter: (a, b) => dayjs(a.fechaInicio).unix() - dayjs(b.fechaInicio).unix(),
      render: (f) => <span className="text-sm text-gray-500 tabular-nums">{f ? dayjs(f).format('DD/MM/YYYY') : '—'}</span>,
    },
    {
      title: <span className="sr-only">Acciones</span>,
      key: 'acciones',
      width: 56,
      align: 'center',
      render: (_, m) => (
        <span onClick={e => e.stopPropagation()}>
          <Dropdown trigger={['click']} placement="bottomRight" menu={{
            items: [
              { key: 'detalle', icon: <EyeOutlined />, label: 'Ver detalle', onClick: () => setDetalle(m) },
              { key: 'historial', icon: <HistoryOutlined />, label: 'Historial de la persona', onClick: () => verHistorial(m) },
              ...(puedeRevocar && sigueActiva(m) ? [
                { type: 'divider' },
                { key: 'revocar', icon: <StopOutlined />, danger: true, label: 'Revocar matrícula', onClick: () => { setRevocar(m); setMotivo(''); } },
              ] : []),
            ],
          }}>
            <button type="button" aria-label={`Acciones de ${m.datosInscrito?.nombre || m.rut}`}
              className="w-9 h-9 inline-flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors">
              <FiMoreVertical size={16} />
            </button>
          </Dropdown>
        </span>
      ),
    },
  ];

  const handleExportarExcel = async () => {
    if (!filtradas.length) return;
    try {
      await exportarHojaUnica({
        archivo: `matriculados-${nombreRecinto.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
        titulo: 'Usuarios matriculados',
        subtitulo: `Matrículas registradas en ${nombreRecinto}`,
        usuario: user,
        filtros: {
          'Recinto': nombreRecinto,
          'Estado': { todas: 'Todas', activas: 'Activas', por_vencer: 'Por vencer', inactivas: 'Vencidas o canceladas' }[vista],
          'Tipo de registro': { todos: 'Todos', pago: 'Pago online', manual: 'Manual' }[registro],
          'Búsqueda': busqueda || null,
        },
        nombreHoja: 'Matriculados',
        columnas: [
          { header: 'Nombre', key: 'nombre', tipo: 'texto', ancho: 34 },
          { header: 'RUT', key: 'rut', tipo: 'texto' },
          { header: 'Email', key: 'email', tipo: 'texto', ancho: 32 },
          { header: 'Teléfono', key: 'telefono', tipo: 'texto' },
          { header: 'Recinto', key: 'recinto', tipo: 'texto', ancho: 26 },
          { header: 'Estado', key: 'estado', tipo: 'texto', resaltar: (u) => (u.estado === 'Vigente' ? 'ok' : 'alerta') },
          { header: 'Tipo de vigencia', key: 'tipoVigencia', tipo: 'texto' },
          { header: 'Tipo de registro', key: 'tipoRegistro', tipo: 'texto' },
          { header: 'Fecha de inicio', key: 'fechaInicio', tipo: 'fecha' },
          { header: 'Fecha de vencimiento', key: 'fechaVencimiento', tipo: 'fecha' },
          { header: 'Días restantes', key: 'diasRestantes', tipo: 'numero' },
          { header: 'Valor pagado', key: 'valorPagado', tipo: 'moneda', total: true },
          { header: 'Orden de compra', key: 'ordenCompra', tipo: 'texto', ancho: 24 },
        ],
        filas: filtradas.map(u => {
          const esManual = tipoRegistro(u) === 'manual';
          return {
            nombre: formatearNombre(u.datosInscrito?.nombre),
            rut: u.rut,
            email: u.datosInscrito?.email,
            telefono: u.datosInscrito?.telefono,
            recinto: nombreRecinto,
            // Antes todo lo que no era vigente salía como "Vencida", también las canceladas.
            estado: ESTADOS[estadoDe(u)].label,
            tipoVigencia: textoVigencia(u),
            tipoRegistro: textoOrigen(u),
            fechaInicio: u.fechaInicio,
            fechaVencimiento: u.fechaVencimiento,
            diasRestantes: u.fechaVencimiento
              ? dayjs(u.fechaVencimiento).startOf('day').diff(dayjs().startOf('day'), 'day')
              : null,
            // Las matrículas manuales no tienen pago asociado: dejar la celda en
            // blanco y no un 0, que distorsionaría el total de la columna.
            valorPagado: esManual ? null : u.valorPagado,
            ordenCompra: u.ordenCompra || (u.ventaPresencial ? 'Venta en caja' : 'Manual'),
          };
        }),
        notas: [
          'Vencimiento en blanco: la matrícula es de vigencia indefinida.',
          'Valor pagado en blanco: registro manual, sin pago asociado.',
        ],
      });
    } catch { notify.error('Error al generar Excel'); }
  };

  return (
    <div className="max-w-[1280px]">
      <EncabezadoAdmin
        titulo="Usuarios"
        destacado="matriculados"
        subtitulo={nombreRecinto ? `${conteo.total.toLocaleString('es-CL')} matrículas en ${nombreRecinto}` : 'Matrículas por recinto'}
      >
        <button type="button" onClick={() => cargar()} disabled={loading} aria-label="Actualizar"
          className="w-10 h-10 inline-flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:border-primary hover:text-primary transition-colors disabled:opacity-60">
          <FiRefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
        <button type="button" onClick={handleExportarExcel} disabled={!filtradas.length} className={claseBtnSecundario}>
          <FiDownload size={15} /> Exportar Excel
        </button>
      </EncabezadoAdmin>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <TarjetaResumen icon={<FiUsers size={19} />} label="Todas" value={conteo.total}
          activa={vista === 'todas'} onClick={() => setVista('todas')} />
        <TarjetaResumen icon={<FiCheckCircle size={19} />} label="Activas" value={conteo.activas}
          activa={vista === 'activas'} onClick={() => setVista('activas')} />
        <TarjetaResumen icon={<FiClock size={19} />} label={`Vencen en ${DIAS_POR_VENCER} días`} value={conteo.porVencer}
          activa={vista === 'por_vencer'} onClick={() => setVista('por_vencer')} />
        <TarjetaResumen icon={<FiXCircle size={19} />} label="Vencidas o canceladas" value={conteo.inactivas}
          activa={vista === 'inactivas'} onClick={() => setVista('inactivas')} />
      </div>

      <div className="flex flex-wrap items-center gap-3 p-3 mb-4 rounded-2xl bg-white border border-gray-100">
        <Select value={recintoId} onChange={(v) => { setRecintoId(v); setVista('todas'); }} style={{ minWidth: 240 }}
          showSearch optionFilterProp="label" aria-label="Recinto" placeholder="Selecciona un recinto"
          options={recintos.map(r => ({ value: r._id, label: r.nombre }))} />
        <Input allowClear prefix={<FiSearch className="text-gray-400" />} placeholder="Buscar por nombre, RUT o email…"
          value={busqueda} onChange={e => setBusqueda(e.target.value)} className="!rounded-full" style={{ maxWidth: 320 }}
          aria-label="Buscar matrícula" />
        <FiltroChips label="Tipo de registro" valor={registro} onChange={setRegistro} opciones={[
          { value: 'todos', label: 'Todos' }, { value: 'pago', label: 'Pago online' }, { value: 'manual', label: 'Manual' },
        ]} />
        {hayFiltros && (
          <button type="button" onClick={() => { setBusqueda(''); setVista('todas'); setRegistro('todos'); }}
            className="text-sm font-semibold text-gray-500 hover:text-primary">
            Limpiar filtros
          </button>
        )}
        <span className="ml-auto text-xs font-semibold text-gray-500">{filtradas.length} resultado{filtradas.length !== 1 ? 's' : ''}</span>
      </div>

      {error && <Alert type="error" showIcon className="mb-4" message={error} />}

      <div className="admin-table-card">
        <Table
          columns={columns}
          dataSource={filtradas}
          loading={loading}
          rowKey="_id"
          sticky
          scroll={{ x: 1000 }}
          rowClassName={() => 'fila-clicable'}
          onRow={(m) => ({ onClick: () => setDetalle(m) })}
          pagination={{
            defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'],
            showTotal: (t, r) => `${r[0]}-${r[1]} de ${t} matrículas`, style: { padding: '12px 16px' },
          }}
          locale={{
            emptyText: (
              <div className="py-10 text-center">
                <p className="font-bold text-gray-900 m-0">
                  {error ? 'No se pudieron cargar las matrículas' : matriculas.length ? 'Ninguna matrícula coincide' : 'Este recinto aún no tiene matrículas'}
                </p>
                <p className="text-sm text-gray-500 m-0 mt-1">
                  {error ? 'Prueba con el botón de actualizar.' : matriculas.length ? 'Prueba con otra búsqueda o limpia los filtros.' : 'Cuando alguien compre o se registre una matrícula aparecerá aquí.'}
                </p>
              </div>
            ),
          }}
        />
      </div>

      {/* ── Detalle ── */}
      <Modal
        open={!!detalle}
        onCancel={() => setDetalle(null)}
        width={680}
        title={detalle && (
          <div className="flex items-center gap-3 pr-8">
            <Iniciales nombre={detalle.datosInscrito?.nombre} className="!w-10 !h-10" />
            <div className="min-w-0">
              <div className="text-base font-extrabold text-gray-900 truncate">{formatearNombre(detalle.datosInscrito?.nombre) || 'Sin nombre'}</div>
              <div className="text-xs font-normal text-gray-500 font-mono">{detalle.rut}</div>
            </div>
          </div>
        )}
        footer={detalle && (
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" onClick={() => verHistorial(detalle)} className={claseBtnSecundario}>
              <HistoryOutlined /> Historial
            </button>
            {puedeRevocar && sigueActiva(detalle) && (
              <button type="button" onClick={() => { setRevocar(detalle); setMotivo(''); }}
                className={`${claseBtnSecundario} hover:!border-red-300 hover:!text-red-600`}>
                <StopOutlined /> Revocar
              </button>
            )}
          </div>
        )}
      >
        {detalle && (() => {
          const v = vencimientoTexto(detalle);
          return (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2"><ChipEstadoMatricula m={detalle} /></div>
              <Descriptions column={{ xs: 1, sm: 2 }} size="small" bordered>
                <Descriptions.Item label="Recinto" span={2}>{detalle.recinto?.nombre || nombreRecinto}</Descriptions.Item>
                <Descriptions.Item label="Email">{detalle.datosInscrito?.email || '—'}</Descriptions.Item>
                <Descriptions.Item label="Teléfono">{detalle.datosInscrito?.telefono || '—'}</Descriptions.Item>
                <Descriptions.Item label="Vigencia">{textoVigencia(detalle)}</Descriptions.Item>
                <Descriptions.Item label="Vencimiento">{v.fecha}{v.dias !== undefined && <span className="text-gray-500"> · {v.detalle}</span>}</Descriptions.Item>
                <Descriptions.Item label="Registrada">{detalle.fechaInicio ? dayjs(detalle.fechaInicio).format('DD/MM/YYYY HH:mm') : '—'}</Descriptions.Item>
                <Descriptions.Item label="Registro">
                  {tipoRegistro(detalle) === 'pago' ? `${textoOrigen(detalle)} · ${formatearMoneda(detalle.valorPagado)}` : `${textoOrigen(detalle)} (sin pago asociado)`}
                </Descriptions.Item>
                {detalle.ultimoMovimiento && (
                  <Descriptions.Item label="Última compra en el recinto" span={2}>
                    {dayjs(detalle.ultimoMovimiento).format('DD/MM/YYYY')}
                    <span className="text-gray-500"> · caduca si no compra nada hasta el {dayjs(detalle.ultimoMovimiento).add(2, 'year').format('DD/MM/YYYY')}</span>
                  </Descriptions.Item>
                )}
                {detalle.ordenCompra && (
                  <Descriptions.Item label="Orden de compra" span={2}><span className="font-mono text-xs">{detalle.ordenCompra}</span></Descriptions.Item>
                )}
              </Descriptions>

              {detalle.desactivadoEn && (
                <Alert
                  type={detalle.estado === 'cancelada' ? 'error' : 'warning'}
                  showIcon
                  icon={<WarningOutlined />}
                  message={detalle.estado === 'cancelada' ? 'Matrícula revocada' : 'Revocación registrada, pero sin aplicar'}
                  description={
                    <div className="space-y-0.5">
                      {detalle.estado !== 'cancelada' && (
                        <div className="mb-1">El servidor guardó la revocación pero no cambió el estado: la matrícula sigue activa y sirve para inscribirse.</div>
                      )}
                      {detalle.motivoDesactivacion && <div><strong>Motivo:</strong> {detalle.motivoDesactivacion}</div>}
                      {detalle.desactivadoPorNombre && <div><strong>Por:</strong> {detalle.desactivadoPorNombre}</div>}
                      <div><strong>Fecha:</strong> {dayjs(detalle.desactivadoEn).format('DD/MM/YYYY HH:mm')}</div>
                    </div>
                  }
                />
              )}
            </div>
          );
        })()}
      </Modal>

      {/* ── Historial ── */}
      <Modal
        open={historial.abierto}
        onCancel={() => setHistorial(h => ({ ...h, abierto: false }))}
        footer={null}
        width={760}
        title={historial.persona && (
          <span>Historial de <span className="hl-amarillo">{formatearNombre(historial.persona.datosInscrito?.nombre) || historial.persona.rut}</span></span>
        )}
      >
        {historial.cargando ? (
          <div className="py-10 text-center"><Spin /></div>
        ) : historial.filas.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500 m-0">Esta persona no tiene otras matrículas.</p>
        ) : (
          <ul className="m-0 p-0 list-none space-y-2">
            {historial.filas.map(h => {
              const v = vencimientoTexto(h);
              return (
                <li key={h._id} className="flex flex-wrap items-center justify-between gap-3 p-3.5 rounded-2xl border border-gray-100">
                  <div className="min-w-0">
                    <p className="font-bold text-gray-900 m-0">{formatearNombre(h.recinto?.nombre) || 'Sin recinto'}</p>
                    <p className="text-xs text-gray-500 m-0 mt-0.5">
                      Desde {h.fechaInicio ? dayjs(h.fechaInicio).format('DD/MM/YYYY') : '—'} · vence {v.fecha.toLowerCase() === 'indefinida' ? 'nunca' : v.fecha}
                      {' · '}{tipoRegistro(h) === 'pago' ? formatearMoneda(h.valorPagado) : 'manual'}
                    </p>
                  </div>
                  <ChipEstadoMatricula m={h} />
                </li>
              );
            })}
          </ul>
        )}
      </Modal>

      {/* ── Revocar ── */}
      <Modal
        open={!!revocar}
        onCancel={() => { setRevocar(null); setMotivo(''); }}
        onOk={confirmarRevocacion}
        okText="Registrar revocación"
        okButtonProps={{ danger: true, loading: revocando, disabled: motivo.trim().length < 5 }}
        cancelText="Cancelar"
        width={500}
        destroyOnClose
        title="Revocar matrícula"
      >
        {revocar && (
          <div className="space-y-4">
            <div className="p-3.5 rounded-2xl bg-gray-50">
              <p className="font-bold text-gray-900 m-0">{formatearNombre(revocar.datosInscrito?.nombre) || 'Sin nombre'}</p>
              <p className="text-xs text-gray-500 m-0">{revocar.rut} · {revocar.recinto?.nombre || nombreRecinto}</p>
            </div>
            {/* Antes decía "desactivará permanentemente la matrícula", y no era cierto. */}
            <Alert
              type="warning"
              showIcon
              message="Hoy la revocación solo queda registrada"
              description="El servidor guarda el motivo, quién y cuándo, pero todavía no cambia el estado de la matrícula: seguirá activa hasta que se corrija en el backend."
            />
            <div>
              <label htmlFor="motivo-revocacion" className="block text-sm font-bold text-gray-900 mb-1.5">
                Motivo <span className="text-red-600">*</span>
              </label>
              <Input.TextArea id="motivo-revocacion"
                placeholder="Ej: baja voluntaria, error en el registro, matrícula duplicada…"
                value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} maxLength={300} showCount
                status={motivo.length > 0 && motivo.trim().length < 5 ? 'error' : ''} />
              {motivo.length > 0 && motivo.trim().length < 5 && (
                <p className="text-xs text-red-600 mt-1 mb-0">Mínimo 5 caracteres.</p>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default UsuariosMatriculados;
